/**
 * Schema Injector Component
 *
 * Injects JSON-LD structured data into the page head.
 * Supports Organization, Article, FAQPage, BreadcrumbList, and custom schemas.
 */

import { Helmet } from 'react-helmet-async';

interface SchemaInjectorProps {
  schema?: Record<string, unknown>;
  type?: 'organization' | 'article' | 'faq' | 'breadcrumb' | 'custom';
  data?: {
    name?: string;
    url?: string;
    logo?: string;
    description?: string;
    headline?: string;
    datePublished?: string;
    dateModified?: string;
    author?: string;
    image?: string;
    questions?: Array<{ question: string; answer: string }>;
    breadcrumbs?: Array<{ name: string; url: string }>;
  };
}

export const SchemaInjector: React.FC<SchemaInjectorProps> = ({ schema, type, data }) => {
  const generateSchema = (): Record<string, unknown> => {
    // If custom schema provided, use it
    if (schema) {
      return schema;
    }

    // Generate schema based on type
    switch (type) {
      case 'organization':
        return {
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: data?.name || 'Rooflyzer OS',
          url: data?.url || 'https://rooflyzer.pro',
          logo: data?.logo || 'https://rooflyzer.pro/logo.png',
          description: data?.description || 'The operating system for modern roofing businesses',
          address: {
            '@type': 'PostalAddress',
            streetAddress: '30 N Gould St #55327',
            addressLocality: 'Sheridan',
            addressRegion: 'WY',
            postalCode: '82801',
            addressCountry: 'US',
          },
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: '+1-370-289-8830',
            contactType: 'Customer Service',
            email: 'support@rooflyzer.pro',
          },
        };

      case 'article':
        return {
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          headline: data?.headline || '',
          description: data?.description || '',
          image: data?.image,
          datePublished: data?.datePublished,
          dateModified: data?.dateModified || data?.datePublished,
          author: {
            '@type': 'Person',
            name: data?.author || 'Rooflyzer Team',
          },
          publisher: {
            '@type': 'Organization',
            name: 'Rooflyzer OS',
            logo: {
              '@type': 'ImageObject',
              url: 'https://rooflyzer.pro/logo.png',
            },
          },
        };

      case 'faq':
        if (!data?.questions || data.questions.length === 0) {
          return {};
        }
        return {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: data.questions.map((q) => ({
            '@type': 'Question',
            name: q.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: q.answer,
            },
          })),
        };

      case 'breadcrumb':
        if (!data?.breadcrumbs || data.breadcrumbs.length === 0) {
          return {};
        }
        return {
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: data.breadcrumbs.map((crumb, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: crumb.name,
            item: crumb.url,
          })),
        };

      default:
        return {};
    }
  };

  const schemaData = generateSchema();

  if (!schemaData || Object.keys(schemaData).length === 0) {
    return null;
  }

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(schemaData, null, 2)}</script>
    </Helmet>
  );
};
